import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BaseGame } from '@shared/types/base-game';
import { GameOptions } from '@shared/types/game-options';
import { tap, map } from 'rxjs/operators';
import { BehaviorSubject, Observable, catchError, throwError } from 'rxjs';

import { environment } from '@environment';
import { addNewItemToState, updateItemInState } from '@shared/utils/state.util';
import { NotificationService } from './notification.service';
import { GetRequestParams } from '@shared/types/request-params';
import { Page } from '@longnecktech/splash-commons-fe';
import { createParamsWithPagination } from '@shared/utils/http.util';

@Injectable({
  providedIn: 'root',
})
export class GameService<TGame extends BaseGame> {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);

  private _games = new BehaviorSubject<TGame[]>([]);
  private _isLoading = new BehaviorSubject<boolean>(false);
  private _totalElements = new BehaviorSubject<number>(0);
  private _gameOptions = new BehaviorSubject<GameOptions[]>([]);

  readonly games$ = this._games.asObservable();
  readonly totalElements$ = this._totalElements.asObservable();
  readonly isLoading$ = this._isLoading.asObservable();
  readonly gamesOptions$ = this._gameOptions.asObservable();
  readonly jackpotOptions$ = this.games$.pipe(
    map((games) => this.getActiveJackpotGames(games)),
  );

  constructor() {}

  fetchGames(params?: GetRequestParams): Observable<Page<TGame>> {
    this._isLoading.next(true);

    if (!params) {
      params = {
        page: 0,
        pageSize: 50,
      };
    }

    const httpParams = createParamsWithPagination(params);
    return this.http
      .get<Page<TGame>>(environment.backendUrl + '/api/v3/admin/game/all', {
        params: httpParams,
      })
      .pipe(
        tap((response) => {
          this._games.next(response.content);
          this._totalElements.next(response.totalElements);
          this._isLoading.next(false);
        }),
        catchError((err: HttpErrorResponse) => {
          this._isLoading.next(false);
          return throwError(
            () => new Error(err?.error?.message || err?.message),
          );
        }),
      );
  }

  fetchGameDetailsV3<TGame>(uuid: string): Observable<TGame> {
    this._isLoading.next(true);
    return this.http
      .get<TGame>(`${environment.backendUrl}/api/v3/admin/game/${uuid}`)
      .pipe(
        tap(() => {
          this._isLoading.next(false);
        }),
        catchError((err: HttpErrorResponse) => {
          this._isLoading.next(false);
          return throwError(
            () => new Error(err?.error?.message || err?.message),
          );
        }),
      );
  }

  fetchGameOptions(finished: boolean): Observable<GameOptions[]> {
    this._isLoading.next(true);
    return this.http
      .get<GameOptions[]>(
        `${environment.backendUrl}/api/admin/game/preview-list`,
        {
          params: {
            finished,
          },
        },
      )
      .pipe(
        tap((options) => {
          this._isLoading.next(false);
          this._gameOptions.next(options);
        }),
        catchError((err: HttpErrorResponse) => {
          this._isLoading.next(false);
          return throwError(
            () => new Error(err?.error?.message || err?.message),
          );
        }),
      );
  }

  fetchGameDetails<TGame>(uuid: string): Observable<TGame> {
    this._isLoading.next(true);
    return this.http
      .get<TGame>(`${environment.backendUrl}/api/admin/game/${uuid}`)
      .pipe(
        tap(() => {
          this._isLoading.next(false);
        }),
        catchError((err: HttpErrorResponse) => {
          this._isLoading.next(false);
          return throwError(
            () => new Error(err?.error?.message || err?.message),
          );
        }),
      );
  }

  saveGameInState(game: TGame, isNew?: boolean): void {
    if (isNew) {
      addNewItemToState(this._games, game);
      this._totalElements.next(this._totalElements.value + 1);
    } else {
      updateItemInState(this._games, game);
    }
  }

  createGame(createGameRequest: TGame): Observable<TGame> {
    this._isLoading.next(true);
    return this.http
      .post<TGame>(
        environment.backendUrl + '/api/admin/game',
        createGameRequest,
      )
      .pipe(
        tap((response) => {
          addNewItemToState(this._games, response);
          this._totalElements.next(this._totalElements.value + 1);
        }),
        tap(() =>
          this.notificationService.showSuccessNotification('Game added'),
        ),
        tap(() => this._isLoading.next(false)),
        catchError((err: Error) => {
          this._isLoading.next(false);
          this.notificationService.showErrorNotification('Error adding game');
          return throwError(
            () => new Error(err?.message || 'Error adding game'),
          );
        }),
      );
  }

  updateGame(game: TGame): Observable<TGame> {
    this._isLoading.next(true);
    return this.http
      .put<TGame>(environment.backendUrl + '/api/admin/game', game)
      .pipe(
        tap((updated) => updateItemInState(this._games, updated)),
        tap(() =>
          this.notificationService.showNotification('game.gameUpdated'),
        ),
        tap(() => this._isLoading.next(false)),
        catchError((err: Error) => {
          this._isLoading.next(false);
          this.notificationService.showNotification('game.errorUpdatingGame');
          return throwError(
            () => new Error(err?.message || 'Error updating game'),
          );
        }),
      );
  }

  settleQuestions(uuid: string): Observable<string> {
    return this.http.put<string>(
      environment.backendUrl +
        `/api/admin/quiz/question/settlement/${uuid}/settle`,
      uuid,
    );
  }

  unsettleQuestions(uuid: string): Observable<string> {
    return this.http.delete<string>(
      environment.backendUrl +
        `/api/admin/quiz/question/settlement/${uuid}/settle`,
    );
  }

  settleLiveLeaderboard(uuid: string): Observable<string> {
    return this.http.put<string>(
      environment.backendUrl +
        `/api/admin/quiz/question/settlement/${uuid}/settle-live-leaderboard`,
      uuid,
    );
  }

  settleGame(game: TGame): Observable<string> {
    return this.http
      .put<string>(
        `${environment.backendUrl}/api/admin/game/${game.uuid}/settle`,
        game.uuid,
      )
      .pipe(
        tap(() => {
          updateItemInState(this._games, {
            ...game,
            settled: true,
          });
        }),
      );
  }

  unsettleGame(game: TGame): Observable<string> {
    return this.http
      .put<string>(
        `${environment.backendUrl}/api/admin/game/${game.uuid}/unsettle`,
        game.uuid,
      )
      .pipe(
        tap(() => {
          updateItemInState(this._games, {
            ...game,
            settled: false,
          });
        }),
      );
  }

  resettleGame(game: TGame): Observable<string> {
    return this.http
      .put<string>(
        `${environment.backendUrl}/api/admin/game/${game.uuid}/resettle`,
        game.uuid,
      )
      .pipe(
        tap(() => {
          updateItemInState(this._games, {
            ...game,
            settled: true,
          });
        }),
      );
  }

  resetData(): void {
    this._games.next([]);
  }

  private getActiveJackpotGames(games: TGame[]): TGame[] {
    return games.filter(
      (game) => !(game && 'won' in game && game.won) && !game.cancelled,
    );
  }
}
